import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import type { ChatMessageInfo, FolderInfo, ProjectDetail, TodayInfo } from "@handvero/shared";
import { api, errorText, upload } from "../api";
import { EmptyState, Icon, Message, Sheet, splitNumber, useUnauthorizedRedirect } from "./ui";

const POLL_MS = 8000;

function stamp(iso: string) {
  const d = new Date(iso);
  const time = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return time;
  if (d.toDateString() === new Date(today.getTime() - 86_400_000).toDateString()) return `Gestern, ${time}`;
  return `${d.toLocaleDateString("de-DE", { day: "numeric", month: "short" })}, ${time}`;
}

function dayLabel(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return "Heute";
  if (d.toDateString() === new Date(today.getTime() - 86_400_000).toDateString()) return "Gestern";
  return d.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" });
}

/** Große Handyfotos vor dem Senden verkleinern; was der Browser nicht lesen kann (z. B. HEIC), geht unverändert raus. */
async function shrink(file: File): Promise<File> {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size < 600_000) return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1800 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d")!.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", 0.82));
    return blob && blob.size < file.size ? new File([blob], "foto.jpg", { type: "image/jpeg" }) : file;
  } catch {
    return file;
  }
}

/** Chat eines Projekts: Absprachen mit den Kollegen, die das Projekt sehen dürfen. */
export function ChatView({ projectId }: { projectId: string }) {
  const onAuth = useUnauthorizedRedirect();
  const [messages, setMessages] = useState<ChatMessageInfo[] | null>(null);
  const [error, setError] = useState("");
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [sending, setSending] = useState(false);
  const [menu, setMenu] = useState<ChatMessageInfo | null>(null);
  const [saving, setSaving] = useState<ChatMessageInfo | null>(null);
  const [notice, setNotice] = useState("");
  const endRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const lastCount = useRef(0);

  const load = useCallback(() => {
    api
      .get<ChatMessageInfo[]>(`/api/projects/${projectId}/chat`)
      .then((m) => {
        setMessages(m);
        setError("");
      })
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [projectId, onAuth]);
  useEffect(() => {
    load();
    const t = setInterval(() => document.visibilityState === "visible" && load(), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (messages && messages.length !== lastCount.current) {
      lastCount.current = messages.length;
      endRef.current?.scrollIntoView({ block: "end" });
    }
  }, [messages]);

  useEffect(() => {
    if (!photo) return setPreview("");
    const url = URL.createObjectURL(photo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  async function send() {
    if (sending || (!text.trim() && !photo)) return;
    setSending(true);
    setError("");
    try {
      let sent: ChatMessageInfo;
      if (photo) {
        const form = new FormData();
        form.append("body", text.trim());
        form.append("photo", await shrink(photo));
        sent = await upload<ChatMessageInfo>(`/api/projects/${projectId}/chat`, form);
      } else {
        sent = await api.post<ChatMessageInfo>(`/api/projects/${projectId}/chat`, { body: text.trim() });
      }
      setMessages((all) => [...(all ?? []), sent]);
      setText("");
      setPhoto(null);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSending(false);
    }
  }

  async function remove(m: ChatMessageInfo) {
    setMenu(null);
    if (!window.confirm(m.hasPhoto ? "Diese Nachricht samt Foto für alle löschen?" : "Diese Nachricht für alle löschen?")) return;
    try {
      await api.del(`/api/chat/${m.id}`);
      setMessages((all) => (all ?? []).filter((x) => x.id !== m.id));
    } catch (err) {
      setError(errorText(err));
    }
  }

  let lastDay = "";
  return (
    <main className="app-main chat">
      <Message>{error}</Message>
      <Message kind="ok">{notice}</Message>
      {!messages && !error && <div className="skel" role="status" aria-label="Wird geladen"><i /><i /></div>}
      {messages?.length === 0 && (
        <EmptyState
          title="Noch keine Nachrichten"
          text="Hier klären Sie Dinge mit den Kollegen, die dieses Projekt sehen. Ein Foto aus dem Chat können Sie mit einem Tipp im Projektordner ablegen."
          words={["Fragen", "Klären"]}
        />
      )}
      <ol className="chat-list" aria-live="polite">
        {messages?.map((m) => {
          const day = dayLabel(m.createdAt);
          const head = day !== lastDay;
          lastDay = day;
          return (
            <li key={m.id} className="chat-row">
              {head && <p className="chat-day">{day}</p>}
              <div className={`chat-msg${m.mine ? " mine" : ""}`}>
                {!m.mine && <b className="chat-who">{m.createdBy ?? "Gelöschter Benutzer"}</b>}
                {m.hasPhoto && (
                  <a href={`/api/chat/${m.id}/photo`} target="_blank" rel="noreferrer">
                    <img className="chat-photo" src={`/api/chat/${m.id}/photo`} alt="Foto im Chat" loading="lazy" />
                  </a>
                )}
                {m.body && <p>{m.body}</p>}
                <span className="chat-time">
                  {stamp(m.createdAt)}
                  {(m.hasPhoto || m.canDelete) && (
                    <button className="chat-more" onClick={() => setMenu(m)} aria-label="Nachricht bearbeiten">
                      <Icon name="more" />
                    </button>
                  )}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
      <div ref={endRef} />
      <form
        className="chat-composer"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        {preview && (
          <div className="chat-attach">
            <img src={preview} alt="Ausgewähltes Foto" />
            <button type="button" className="ab-btn small" onClick={() => setPhoto(null)} aria-label="Foto entfernen">
              <Icon name="close" />
            </button>
          </div>
        )}
        <div className="chat-bar">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              setPhoto(e.target.files?.[0] ?? null);
              e.target.value = "";
            }}
          />
          <button type="button" className="ab-btn" onClick={() => fileRef.current?.click()} aria-label="Foto anhängen">
            <Icon name="camera" />
          </button>
          <textarea
            value={text}
            rows={1}
            maxLength={2000}
            placeholder="Nachricht an die Kollegen"
            aria-label="Nachricht"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && window.matchMedia("(hover: hover)").matches) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <button type="submit" className="chat-send" disabled={sending || (!text.trim() && !photo)} aria-label="Senden">
            <Icon name="send" />
          </button>
        </div>
      </form>
      {menu && (
        <Sheet title="Nachricht" onClose={() => setMenu(null)}>
          <div className="menu">
            {menu.hasPhoto && (
              <button
                onClick={() => {
                  setSaving(menu);
                  setMenu(null);
                }}
              >
                Foto im Projektordner ablegen
              </button>
            )}
            {menu.canDelete && <button onClick={() => void remove(menu)}>Löschen</button>}
          </div>
        </Sheet>
      )}
      {saving && (
        <SaveToFolder
          projectId={projectId}
          message={saving}
          onClose={() => setSaving(null)}
          onSaved={(name, folder) => {
            setSaving(null);
            setNotice(`Das Foto liegt jetzt als „${name}“ im Ordner „${folder}“.`);
          }}
        />
      )}
    </main>
  );
}

function SaveToFolder({ projectId, message, onClose, onSaved }: { projectId: string; message: ChatMessageInfo; onClose: () => void; onSaved: (name: string, folder: string) => void }) {
  const [folders, setFolders] = useState<FolderInfo[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api
      .get<ProjectDetail>(`/api/projects/${projectId}`)
      .then((p) => setFolders(p.folders))
      .catch((err) => setError(errorText(err)));
  }, [projectId]);

  // Ordnerbaum als Pfade („Anlage › Fotos“), damit gleiche Namen unterscheidbar bleiben.
  const paths = (() => {
    if (!folders) return [];
    const byId = new Map(folders.map((f) => [f.id, f]));
    const label = (f: FolderInfo): string => (f.parentId && byId.get(f.parentId) ? `${label(byId.get(f.parentId)!)} › ` : "") + splitNumber(f.name).label;
    return folders.map((f) => ({ id: f.id, text: label(f) })).sort((a, b) => a.text.localeCompare(b.text, "de"));
  })();

  async function pick(f: { id: string; text: string }) {
    try {
      const res = await api.post<{ name: string }>(`/api/chat/${message.id}/save`, { folderId: f.id });
      onSaved(res.name, f.text);
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <Sheet title="In welchen Ordner?" onClose={onClose}>
      <Message>{error}</Message>
      {folders && paths.length === 0 && <p className="app-empty small">Dieses Projekt hat noch keine Ordner. Legen Sie zuerst einen an.</p>}
      <div className="menu">
        {paths.map((f) => (
          <button key={f.id} onClick={() => void pick(f)}>
            {f.text}
          </button>
        ))}
      </div>
    </Sheet>
  );
}

/** „Heute“: Termine, neue Chat-Nachrichten und neue Dateien der Kollegen auf einen Blick. */
export function TodayPanel({ base, compact = false }: { base: "/app" | "/desktop"; compact?: boolean }) {
  const [today, setToday] = useState<TodayInfo | null>(null);
  useEffect(() => {
    const load = () =>
      api
        .get<TodayInfo>("/api/today")
        .then(setToday)
        .catch(() => undefined);
    load();
    const t = setInterval(() => document.visibilityState === "visible" && load(), 30_000);
    return () => clearInterval(t);
  }, []);
  if (!today) return null;
  const empty = !today.unreadChats.length && !today.events.length && !today.newFiles.length;
  if (empty && compact) return null;
  const time = (iso: string) => new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  return (
    <section className={`today${compact ? " compact" : ""}`} aria-label="Heute">
      <h2>Heute</h2>
      {empty && <p className="muted-s">Alles ruhig: keine Termine heute, keine neuen Nachrichten.</p>}
      {today.unreadChats.length > 0 && (
        <div className="today-block">
          <h3>Neu im Chat</h3>
          <ul>
            {today.unreadChats.map((c) => (
              <li key={c.projectId}>
                <Link to={`${base}/p/${c.projectId}/chat`}>
                  <span className="today-dot">{c.count}</span>
                  <span>
                    <b>{c.projectName}</b>
                    <small>{c.count === 1 ? "1 neue Nachricht" : `${c.count} neue Nachrichten`}</small>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {today.events.length > 0 && (
        <div className="today-block">
          <h3>Termine heute</h3>
          <ul>
            {today.events.map((e) => (
              <li key={e.id}>
                <Link to={e.projectId ? `${base}/p/${e.projectId}/termine` : `${base}/kalender`}>
                  <span className="today-time">{e.allDay ? "ganztägig" : time(e.startsAt)}</span>
                  <span>
                    <b>{e.title}</b>
                    {e.projectName && <small>{e.projectName}</small>}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {today.newFiles.length > 0 && !compact && (
        <div className="today-block">
          <h3>Neu von den Kollegen</h3>
          <ul>
            {today.newFiles.map((f) => (
              <li key={f.id}>
                <Link to={`${base}/p/${f.projectId}`}>
                  <span className="today-time">{f.kind === "photo" ? "Foto" : "Datei"}</span>
                  <span>
                    <b>{f.name}</b>
                    <small>
                      {f.projectName}
                      {f.createdBy ? ` · ${f.createdBy}` : ""}
                    </small>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
